/**
 * Notifications contract (docs/09-notifications.md §9). N1 (API) implements it, N2 (web) consumes it.
 * Keep shapes stable: changing one here is a contract change for both sides.
 *
 * Naming: the legacy `/api/v1/messaging/notifications` shapes (NotificationSchema, NotificationListSchema,
 * NOTIFICATION_TYPES in ./messaging.ts) are kept for the old routes; everything for the new
 * `/api/v1/notifications` centre is below.
 */
import { z } from 'zod';
import { EpochMsSchema, UlidSchema } from './common';

// ------------------------------------------------------------ vocabulary

export const NOTIFICATION_CATEGORIES = [
  'network',
  'messages',
  'content',
  'jobs',
  'hiring',
  'ads_marketing',
  'agents_approvals',
  'groups_events',
  'account_security',
] as const;
export const NotificationCategorySchema = z.enum(NOTIFICATION_CATEGORIES);
export type NotificationCategory = z.infer<typeof NotificationCategorySchema>;

/** Human labels for the category × channel matrix (§7). */
export const NOTIFICATION_CATEGORY_LABELS: Record<NotificationCategory, string> = {
  network: 'Network',
  messages: 'Messages',
  content: 'Content',
  jobs: 'Jobs',
  hiring: 'Hiring',
  ads_marketing: 'Ads & marketing',
  agents_approvals: 'Agents & approvals',
  groups_events: 'Groups & events',
  account_security: 'Account & security',
};

/** Categories the user cannot turn off (security is locked on, §7). */
export const LOCKED_CATEGORIES: NotificationCategory[] = ['account_security'];

export const NOTIFICATION_PRIORITIES = ['critical', 'high', 'normal', 'low'] as const;
export const NotificationPrioritySchema = z.enum(NOTIFICATION_PRIORITIES);
export type NotificationPriority = z.infer<typeof NotificationPrioritySchema>;

/** Channels a user controls per category/type. Real-time follows in-app. Web push is N3 (not built). */
export const USER_NOTIFICATION_CHANNELS = ['in_app', 'email'] as const;

/** Every delivery channel that can appear in the delivery log. */
export const NOTIFICATION_CHANNELS = [
  'in_app',
  'realtime',
  'email',
  'agent_inbox',
  'mcp',
  'webhook',
] as const;
export const NotificationChannelSchema = z.enum(NOTIFICATION_CHANNELS);
export type NotificationChannel = z.infer<typeof NotificationChannelSchema>;

export const NOTIFICATION_DELIVERY_STATUSES = ['queued', 'sent', 'failed', 'suppressed'] as const;
export const NotificationDeliveryStatusSchema = z.enum(NOTIFICATION_DELIVERY_STATUSES);

/** Every event type of the §6 matrix (plus pipeline-generated owner notices). Category per type. */
export const NOTIFICATION_TYPE_CATEGORY = {
  'approval.requested': 'agents_approvals',
  'approval.decided': 'agents_approvals',
  'approval.expiring': 'agents_approvals',
  'guardrail.blocked': 'agents_approvals',
  'agent.token_expiring': 'agents_approvals',
  'agent.activity_summary': 'agents_approvals',
  'agent.rate_limited': 'agents_approvals',
  'agent.unacknowledged': 'agents_approvals',
  'connection.request': 'network',
  'connection.accept': 'network',
  'invite.accept': 'network',
  'follow.new': 'network',
  'endorsement': 'network',
  'recommendation.received': 'network',
  'message.new': 'messages',
  'post.mention': 'content',
  'comment.new': 'content',
  'post.reaction': 'content',
  'comment.reaction': 'content',
  'post.repost': 'content',
  'application.submitted': 'hiring',
  'application.status_changed': 'jobs',
  'screening.completed': 'hiring',
  'job_alert.matches': 'jobs',
  'job.expiring': 'hiring',
  'campaign.budget_exhausted': 'ads_marketing',
  'campaign.daily_cap_hit': 'ads_marketing',
  'campaign.paused': 'ads_marketing',
  'link.conversion_milestone': 'ads_marketing',
  'webhook.disabled': 'account_security',
  'group.join_requested': 'groups_events',
  'event.reminder': 'groups_events',
  'verification.completed': 'account_security',
  'verification.failed': 'account_security',
  'report.created': 'account_security',
  'report.resolved': 'account_security',
  'security.new_signin': 'account_security',
  'security.token_created': 'account_security',
  'security.identity_linked': 'account_security',
  'account.suspended': 'account_security',
} as const satisfies Record<string, NotificationCategory>;

export type NotificationEventType = keyof typeof NOTIFICATION_TYPE_CATEGORY;
export const NOTIFICATION_EVENT_TYPES = Object.keys(
  NOTIFICATION_TYPE_CATEGORY,
) as NotificationEventType[];
export const NotificationEventTypeSchema = z.enum(
  NOTIFICATION_EVENT_TYPES as [NotificationEventType, ...NotificationEventType[]],
);

/**
 * Event types an owner may delegate to an agent token beyond the outcomes of its own actions
 * (`PUT /agents/tokens/:id/subscriptions`). Security/account types are never delegatable.
 */
export const AGENT_DELEGATABLE_TYPES = [
  'connection.request',
  'connection.accept',
  'message.new',
  'post.mention',
  'comment.new',
  'application.submitted',
  'application.status_changed',
  'screening.completed',
  'job_alert.matches',
  'job.expiring',
  'campaign.budget_exhausted',
  'campaign.daily_cap_hit',
  'campaign.paused',
  'link.conversion_milestone',
  'group.join_requested',
  'event.reminder',
] as const satisfies readonly NotificationEventType[];
export type AgentDelegatableType = (typeof AGENT_DELEGATABLE_TYPES)[number];

/** Event types an agent always receives about its own actions (not configurable). */
export const AGENT_OWN_OUTCOME_TYPES = [
  'approval.decided',
  'guardrail.blocked',
  'application.status_changed',
] as const satisfies readonly NotificationEventType[];

// ------------------------------------------------------------ humans: the notification centre

export const NotificationActorSchema = z.object({
  id: z.string(),
  name: z.string(),
  handle: z.string().nullable(),
  avatarUrl: z.string().nullable(),
});
export const NotificationViaAgentSchema = z.object({
  tokenId: UlidSchema,
  name: z.string(),
  ownerName: z.string(),
});
export const NotificationEntitySchema = z.object({ kind: z.string(), id: z.string() });

/** `type` is a NotificationEventType; kept as string so rows written before a type was renamed still parse. */
export const NotificationItemSchema = z.object({
  id: UlidSchema,
  type: z.string(),
  category: NotificationCategorySchema,
  priority: NotificationPrioritySchema,
  title: z.string(),
  body: z.string(),
  link: z.string().nullable(),
  actors: z.array(NotificationActorSchema),
  count: z.number().int().positive(),
  viaAgent: NotificationViaAgentSchema.nullable(),
  entity: NotificationEntitySchema.nullable(),
  createdAt: EpochMsSchema,
  seenAt: EpochMsSchema.nullable(),
  readAt: EpochMsSchema.nullable(),
  actedAt: EpochMsSchema.nullable(),
});
export type NotificationItem = z.infer<typeof NotificationItemSchema>;

const flag = z
  .enum(['1', '0', 'true', 'false'])
  .optional()
  .transform((v) => v === '1' || v === 'true');

export const NotificationCenterQuerySchema = z.object({
  cursor: z.string().min(1).max(512).optional(),
  category: NotificationCategorySchema.optional(),
  unread: flag,
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
/**
 * `unreadCount` is the BADGE: items not yet seen (`seenAt === null`). `POST /seen` clears it; `POST
 * /read` implies seen. `readAt` separately means "opened"; `?unread=1` filters on `readAt === null`.
 */
export const NotificationCenterSchema = z.object({
  items: z.array(NotificationItemSchema),
  nextCursor: z.string().nullable(),
  unreadCount: z.number().int().nonnegative(),
});

/** `upTo`: a notification id, or an epoch-ms timestamp; everything created at/before it is marked seen. */
export const NotificationsSeenBodySchema = z.object({ upTo: z.union([UlidSchema, EpochMsSchema]) });
export const NotificationsReadBodySchema = z.union([
  z.object({ ids: z.array(UlidSchema).min(1).max(100) }).strict(),
  z.object({ all: z.literal(true) }).strict(),
]);
/** Response of seen / read. `unreadCount` = the badge (unseen items), as in NotificationCenterSchema. */
export const NotificationsUpdatedSchema = z.object({
  updated: z.number().int().nonnegative(),
  unreadCount: z.number().int().nonnegative(),
});
export const NotificationIdParamsSchema = z.object({ id: UlidSchema });
export const NotificationActedSchema = z.object({ item: NotificationItemSchema });

// ------------------------------------------------------------ settings (§7)

const HHMM = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:MM, 24-hour');

function isIanaZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}
export const TimezoneSchema = z.string().min(1).max(64).refine(isIanaZone, 'Unknown IANA time zone');

export const QuietHoursSchema = z.object({ start: HHMM, end: HHMM });
export const DigestCadenceSchema = z.enum(['daily', 'weekly', 'off']);
export const AgentOriginatedSettingSchema = z.enum(['all', 'connections', 'off']);

export const NotificationGlobalSettingsSchema = z.object({
  /** null = no quiet hours. */
  quietHours: QuietHoursSchema.nullable(),
  timezone: TimezoneSchema,
  digest: DigestCadenceSchema,
  highOnlyEmail: z.boolean(),
  agentOriginated: AgentOriginatedSettingSchema,
});
export const NotificationCategorySettingSchema = z.object({
  category: NotificationCategorySchema,
  inApp: z.boolean(),
  email: z.boolean(),
  /** true for account_security: always on, PUT refuses to turn it off. */
  locked: z.boolean(),
});
export const NotificationTypeOverrideSchema = z.object({
  type: NotificationEventTypeSchema,
  inApp: z.boolean(),
  email: z.boolean(),
});
export const NotificationSettingsSchema = z.object({
  global: NotificationGlobalSettingsSchema,
  categories: z.array(NotificationCategorySettingSchema),
  overrides: z.array(NotificationTypeOverrideSchema),
});
export type NotificationSettings = z.infer<typeof NotificationSettingsSchema>;

const categoryUpdate = z
  .object({ category: NotificationCategorySchema, inApp: z.boolean(), email: z.boolean() })
  .refine(
    (v) => !LOCKED_CATEGORIES.includes(v.category) || (v.inApp && v.email),
    'Account & security notifications cannot be turned off',
  );
const overrideUpdate = NotificationTypeOverrideSchema.refine(
  (v) => NOTIFICATION_TYPE_CATEGORY[v.type] !== 'account_security' || (v.inApp && v.email),
  'Account & security notifications cannot be turned off',
);
/** PUT body: every part optional; categories/overrides listed are upserted, others untouched. */
export const NotificationSettingsUpdateSchema = z
  .object({
    global: NotificationGlobalSettingsSchema.partial().optional(),
    categories: z.array(categoryUpdate).max(NOTIFICATION_CATEGORIES.length).optional(),
    overrides: z.array(overrideUpdate).max(NOTIFICATION_EVENT_TYPES.length).optional(),
    /** Types whose per-type override should be removed (fall back to the category). */
    clearOverrides: z.array(NotificationEventTypeSchema).max(NOTIFICATION_EVENT_TYPES.length).optional(),
  })
  .strict();

// ------------------------------------------------------------ mutes

export const MUTE_TARGET_KINDS = [
  'thread',
  'post',
  'group',
  'person',
  'agent_token',
  'agent_owner',
] as const;
export const MuteTargetKindSchema = z.enum(MUTE_TARGET_KINDS);
export const NotificationMuteSchema = z.object({
  targetKind: MuteTargetKindSchema,
  targetId: z.string().min(1).max(128),
  /** epoch ms; null = until removed. */
  until: EpochMsSchema.nullable(),
  createdAt: EpochMsSchema,
});
export const NotificationMuteCreateSchema = z.object({
  targetKind: MuteTargetKindSchema,
  targetId: z.string().min(1).max(128),
  until: EpochMsSchema.nullable().default(null),
});
/** DELETE /notifications/mutes?targetKind=&targetId= */
export const NotificationMuteDeleteQuerySchema = z.object({
  targetKind: MuteTargetKindSchema,
  targetId: z.string().min(1).max(128),
});
export const NotificationMuteListSchema = z.object({ items: z.array(NotificationMuteSchema) });
export const NotificationMuteRemovedSchema = z.object({ removed: z.number().int().nonnegative() });

// ------------------------------------------------------------ delivery log

export const NotificationDeliverySchema = z.object({
  id: UlidSchema,
  notificationId: UlidSchema.nullable(),
  agentNotificationSeq: z.number().int().nullable(),
  agentTokenId: UlidSchema.nullable(),
  /** Event type (present even when no in-app row exists, e.g. a suppressed delivery). */
  type: z.string(),
  channel: NotificationChannelSchema,
  status: NotificationDeliveryStatusSchema,
  /** e.g. email_not_configured, quiet_hours, digest, blocked, muted, pref_off, agent_cap, no_email. */
  reason: z.string().nullable(),
  attempts: z.number().int().nonnegative(),
  sentAt: EpochMsSchema.nullable(),
  createdAt: EpochMsSchema,
});
export const NotificationDeliveriesQuerySchema = z.object({
  notificationId: UlidSchema.optional(),
  cursor: z.string().min(1).max(512).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const NotificationDeliveryListSchema = z.object({
  items: z.array(NotificationDeliverySchema),
  nextCursor: z.string().nullable(),
});

// ------------------------------------------------------------ real-time

/** Messages on GET /api/v1/notifications/stream (WebSocket frames or SSE `data:` lines). */
export const NotificationStreamMessageSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('notification'), item: NotificationItemSchema }),
  /** the badge: items not yet seen (sent on connect, on new items, and after seen / read / act) */
  z.object({ type: z.literal('count'), unreadCount: z.number().int().nonnegative() }),
  z.object({ type: z.literal('ping') }),
]);
export type NotificationStreamMessage = z.infer<typeof NotificationStreamMessageSchema>;

// ------------------------------------------------------------ agents

export const AgentNotificationSchema = z.object({
  seq: z.number().int().positive(),
  type: z.string(),
  payload: z.record(z.string(), z.unknown()),
  createdAt: EpochMsSchema,
  ackedAt: EpochMsSchema.nullable(),
});
export type AgentNotification = z.infer<typeof AgentNotificationSchema>;
export const AgentNotificationListQuerySchema = z.object({
  /** Opaque; it is the last seq seen. Items with a greater seq are returned, ascending. */
  cursor: z.string().regex(/^\d+$/).optional(),
  unacked: flag,
  limit: z.coerce.number().int().min(1).max(100).default(50),
  /** Human session only (Agent Console): which of your tokens' inbox to read. Agents read their own. */
  tokenId: UlidSchema.optional(),
});
export const AgentNotificationListSchema = z.object({
  items: z.array(AgentNotificationSchema),
  nextCursor: z.string().nullable(),
});
export const AgentNotificationsAckSchema = z.object({
  upToSeq: z.number().int().positive(),
  /** Human session only (Agent Console): which of your tokens' inbox to ack. Agents ack their own. */
  tokenId: UlidSchema.optional(),
});
export const AgentNotificationsAckedSchema = z.object({ acked: z.number().int().nonnegative() });

/** Messages on GET /api/v1/agents/me/notifications/stream (SSE). */
export const AgentStreamMessageSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('notification'), item: AgentNotificationSchema }),
  z.object({ type: z.literal('ping') }),
]);

export const AgentSubscriptionsUpdateSchema = z.object({
  types: z.array(z.enum(AGENT_DELEGATABLE_TYPES)).max(AGENT_DELEGATABLE_TYPES.length),
});
export const AgentSubscriptionsSchema = z.object({
  tokenId: UlidSchema,
  types: z.array(z.enum(AGENT_DELEGATABLE_TYPES)),
  /** Always delivered regardless of subscriptions. */
  ownOutcomeTypes: z.array(z.string()),
});
export const AgentTokenIdParamsSchema = z.object({ id: UlidSchema });
